// Shared helpers for cascades that ask the reviewer to pick an account from
// the chart of accounts. The "pick an account" flow repeats across most CA/EQ/
// AR/PRE/INT/TAX/DEBT cascades: keyword-match for candidates, format as prompt
// options, and on click look up the picked account to build a mapping proposal.

export function findAccountByNumber(chartOfAccounts, accountNumber) {
	return (chartOfAccounts || []).find(function (a) {
		return (a.accountNumber || a.AC_NO || "") === accountNumber;
	});
}

export function accountDisplayName(acct, fallback) {
	if (!acct) return fallback;
	return acct.description || acct.accountDescription || acct.AC_DESC || fallback;
}

export function accountToOption(acct) {
	let num = acct.accountNumber || acct.AC_NO || "";
	let desc = acct.description || acct.accountDescription || acct.AC_DESC || "";
	return { id: "link_" + num, label: desc + (num && !num.includes(".") ? " (" + num + ")" : "") };
}

export function findAccountsByKeywords(chartOfAccounts, keywords) {
	return (chartOfAccounts || []).filter(function (acct) {
		let desc = (acct.description || acct.accountDescription || "").toLowerCase();
		return keywords.some(function (kw) { return desc.includes(kw); });
	});
}

// Drives the entire "pick an account from the chart" prompt step.
//
// Three outcomes:
//   - User has already picked: returns the step with inline_mapping ready to propose.
//   - Nothing to show: returns a blocked step.
//   - Otherwise: returns a waiting_for_human step with candidate options
//     (keyword-matched, or all accounts if the reviewer asked to show all).
//
// linkedAccount = userResponses["<resolve_step_id>"]:
//   - "link_<n>" -> mapping proposal
//   - "show_all" -> show every account in the chart
//   - undefined  -> show keyword candidates with a "show all" fallback option
//
// options = {
//   chartOfAccounts, keywords, target: { canonicalCode, canonicalName },
//   question,
//   foundNoun?,      // noun phrase used after "Found N " (default "matching account(s)")
//   allDetail?,      // detail text when showing every account     (default "Showing all accounts")
//   blockedDetail?,  // detail text when there are no accounts at all (default "No accounts available — rerun compilation")
// }
export function pickAccountStep(resolved, linkedAccount, options) {
	let chartOfAccounts = options.chartOfAccounts;
	let keywords = options.keywords;
	let target = options.target;
	let question = options.question;

	if (linkedAccount && linkedAccount.startsWith("link_")) {
		let accountNum = linkedAccount.replace("link_", "");
		let matchedAcct = findAccountByNumber(chartOfAccounts, accountNum);
		return {
			...resolved, status: "ready_to_propose", detail: null,
			inline_mapping: {
				accountNumber: accountNum,
				accountName: accountDisplayName(matchedAcct, accountNum),
				canonicalCode: target.canonicalCode,
				canonicalName: target.canonicalName,
			},
		};
	}

	let showAll = linkedAccount === "show_all";
	let candidates = showAll ? [] : findAccountsByKeywords(chartOfAccounts, keywords);
	let displayAccounts = candidates.length > 0 ? candidates : (chartOfAccounts || []);
	if (displayAccounts.length === 0) {
		return { ...resolved, status: "blocked", detail: options.blockedDetail || "No accounts available — rerun compilation" };
	}
	let accountOptions = displayAccounts.map(accountToOption);
	if (candidates.length > 0) {
		accountOptions.push({ id: "show_all", label: "None of these — show all accounts" });
	}
	return {
		...resolved, status: "waiting_for_human",
		detail: candidates.length > 0
			? ("Found " + candidates.length + " " + (options.foundNoun || "matching account(s)"))
			: (options.allDetail || "Showing all accounts"),
		prompt: { question: question, options: accountOptions },
	};
}
